import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { KineticText } from "@/components/KineticText";
import { Reveal, Tag } from "@/components/Reveal";
import { ParallaxImage } from "@/components/ParallaxImage";
import { Marquee } from "@/components/Marquee";
import { Counter } from "@/components/Counter";
import { IMG } from "@/data/images";

const CHAPTERS = [
  { n: "01", title: "The Problem", to: "/the-problem", note: "Why freshness fails" },
  { n: "02", title: "The Solution", to: "/our-solution", note: "Naturally longer life" },
  { n: "03", title: "The Science", to: "/technology", note: "Inside the technology" },
  { n: "04", title: "The Business", to: "/for-business", note: "Built for kitchens & retail" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-32 lg:pt-40 pb-16 min-h-[92vh] flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center w-full">
          <div className="lg:col-span-7 relative z-10">
            <Reveal y={16}>
              <Tag>Saudi FoodTech — Riyadh</Tag>
            </Reveal>
            <h1 className="mt-7 font-display font-extrabold tracking-tight leading-[1.0] text-5xl sm:text-6xl lg:text-7xl text-forest">
              <KineticText
                delay={0.1}
                lines={[
                  <>Freshness,</>,
                  <>
                    engineered{" "}
                    <span className="font-serif italic font-medium text-emerald">to last.</span>
                  </>,
                ]}
              />
            </h1>
            <Reveal delay={0.5} className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-moss">
              Fresh Guard is building food technology to extend the natural shelf life of
              vegetables and fruits — combining food science, agricultural research, smart
              packaging and supply-chain intelligence to cut waste from farm to fork.
            </Reveal>
            <Reveal delay={0.65} className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/technology"
                data-testid="hero-cta-science"
                className="inline-flex items-center gap-2 rounded-full bg-forest text-cream px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-emerald"
              >
                Explore the science
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-partner"
                className="inline-flex items-center gap-2 rounded-full border border-forest/25 text-forest px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:border-forest hover:bg-mint/40"
              >
                Partner with us
              </Link>
            </Reveal>
            <Reveal delay={0.8} className="mt-14 font-mono text-[10px] uppercase tracking-[0.25em] text-fog">
              Food science · Agricultural research · Smart packaging · Supply-chain intelligence
            </Reveal>
          </div>

          <div className="lg:col-span-5 relative">
            <span className="pointer-events-none select-none absolute -bottom-10 -left-6 font-serif italic text-[9rem] leading-none text-forest/5 z-0">
              fresh
            </span>
            <Reveal delay={0.35} y={40}>
              <ParallaxImage
                src={IMG.asparagus}
                alt="Fresh green asparagus spears on a clean white background"
                ratio="aspect-[4/5]"
                speed={8}
                className="relative z-10 shadow-2xl shadow-forest/10"
                testId="hero-image-main"
              />
            </Reveal>
            <Reveal delay={0.55} y={30} className="absolute -left-8 sm:-left-14 bottom-8 z-20 w-36 sm:w-48">
              <ParallaxImage
                src={IMG.stillLifeDark}
                alt="Premium still life of freshly harvested vegetables"
                ratio="aspect-square"
                speed={14}
                className="border-4 border-cream shadow-xl shadow-forest/20"
                testId="hero-image-secondary"
              />
            </Reveal>
            <Reveal
              delay={0.75}
              y={20}
              className="absolute -top-4 -right-2 sm:-right-6 z-20 bg-white border border-forest/10 shadow-lg shadow-forest/10 px-5 py-4 max-w-[190px]"
            >
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-fog">Pilot-stage target</p>
              <p className="mt-1 font-display font-bold text-forest leading-snug">
                Up to 2× usable freshness
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee
        items={[
          "Science-backed preservation",
          "Extended shelf life",
          "Safety-led R&D",
          "Saudi Vision 2030",
          "Food security",
          "Farm to fork",
        ]}
      />

      {/* IMPACT COUNTERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 sm:py-32">
        <Reveal>
          <Tag>The scale of the challenge</Tag>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-display font-extrabold tracking-tight leading-[1.1] text-3xl sm:text-4xl lg:text-5xl text-forest max-w-3xl">
            Food loss is one of the largest solvable problems of our decade.
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          <Reveal delay={0.05}>
            <Counter value={33} suffix="%" label="of food produced globally is lost or wasted" sub="FAO estimate" testId="impact-counter-global-waste" />
          </Reveal>
          <Reveal delay={0.15}>
            <Counter value={40} prefix="SAR " suffix="B+" label="estimated annual value of food loss and waste in Saudi Arabia" sub="National studies" testId="impact-counter-ksa-cost" />
          </Reveal>
          <Reveal delay={0.25}>
            <Counter value={40} suffix="%+" label="reported loss and waste rate for vegetables in Saudi Arabia" sub="Published national estimates" testId="impact-counter-postharvest" />
          </Reveal>
          <Reveal delay={0.35}>
            <Counter value={2} suffix="×" label="usable freshness — Fresh Guard's pilot-stage target" sub="R&D target, in validation" testId="impact-counter-freshness-target" />
          </Reveal>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="border-t border-forest/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-24 pb-10">
          <Reveal>
            <Tag>The Fresh Guard manifesto</Tag>
          </Reveal>
        </div>
        <div>
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05}>
              <Link
                to={c.to}
                data-testid={`manifesto-link-${c.n}`}
                className="group block border-t border-forest/10 last:border-b transition-colors duration-300 hover:bg-forest"
              >
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-8 sm:py-10 flex items-center justify-between gap-6">
                  <div className="flex items-baseline gap-6 sm:gap-10">
                    <span className="font-mono text-xs text-amber">{c.n}</span>
                    <span className="font-display font-extrabold tracking-tight text-3xl sm:text-5xl text-forest transition-colors group-hover:text-cream">
                      {c.title}
                    </span>
                  </div>
                  <div className="flex items-center gap-6">
                    <span className="hidden md:block font-mono text-[10px] uppercase tracking-[0.2em] text-fog group-hover:text-sage transition-colors">
                      {c.note}
                    </span>
                    <ArrowUpRight className="h-6 w-6 text-forest/40 transition-all group-hover:text-mint group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PRODUCE GALLERY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 sm:py-32">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-start">
          <Reveal>
            <ParallaxImage src={IMG.market} alt="Abundant display of fresh market vegetables" ratio="aspect-[3/4]" speed={9} caption="Source — fresh at origin" testId="gallery-image-source" />
          </Reveal>
          <Reveal delay={0.15} className="md:mt-16">
            <ParallaxImage src={IMG.saladBowl} alt="Premium salad bowl of colourful fresh vegetables" ratio="aspect-[3/4]" speed={12} caption="Protect — naturally longer life" testId="gallery-image-protect" />
          </Reveal>
          <Reveal delay={0.3} className="md:mt-32">
            <ParallaxImage src={IMG.flatlay} alt="Curated flat lay of vibrant fresh produce" ratio="aspect-[3/4]" speed={7} caption="Deliver — ready to use" testId="gallery-image-deliver" />
          </Reveal>
        </div>
      </section>

      {/* SOLUTION PREVIEW */}
      <section className="bg-white border-y border-forest/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <ParallaxImage src={IMG.cooking} alt="Chef preparing dishes with fresh crisp vegetables" ratio="aspect-[4/3]" speed={8} testId="solution-preview-image" />
          </Reveal>
          <div>
            <Reveal><Tag>For kitchens that can&rsquo;t wait</Tag></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display font-extrabold tracking-tight leading-[1.1] text-3xl sm:text-4xl text-forest">
                Ready-to-use produce that stays ready, longer.
              </h2>
            </Reveal>
            <Reveal delay={0.2} className="mt-6 text-base sm:text-lg leading-relaxed text-moss space-y-4">
              <p>
                Restaurants, hotels and caterers lose margin every time produce wilts before
                service. Fresh Guard is being developed to give professional kitchens a longer
                window of peak freshness — fewer emergency orders, less prep waste, more
                consistency.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link
                to="/for-business"
                data-testid="solution-preview-link"
                className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-emerald hover:text-forest transition-colors"
              >
                See what it means for your business
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INVESTOR BAND */}
      <section className="bg-deep text-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 sm:py-32 text-center">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-sage">Investors & strategic partners</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-8 font-display font-extrabold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl max-w-4xl mx-auto">
              The future of fresh is being built{" "}
              <span className="font-serif italic font-medium text-mint">in Riyadh.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.25} className="mt-12 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              data-testid="investor-cta-contact"
              className="inline-flex items-center gap-2 rounded-full bg-mint text-forest px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-cream"
            >
              Start a conversation
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
